import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { Download, FileText } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useLibroCompras } from "~/hooks/purchasing";
import { formatCurrency } from "~/utils/helpers";

import { exportLibroComprasToExcel } from "./utils/exportLibroCompras";
import {
  buildLibroComprasReport,
  formatRutWithDots,
} from "./utils/libroCompras";

import type {
  LibroComprasAmounts,
  LibroComprasSection,
} from "./utils/libroCompras";

const clp = (n: number) => `$${formatCurrency(n)}`;
const clpOrDash = (n: number) => (n ? clp(n) : "-");
const fmtDate = (d: string) => dayjs(d).format("DD/MM/YYYY");

/** Right-aligned amount cell; negatives (notas de crédito) in red. */
function AmountCell({
  value,
  dash = true,
  bold = false,
}: {
  value: number;
  dash?: boolean;
  bold?: boolean;
}) {
  return (
    <TableCell
      className={`text-right tabular-nums ${bold ? "font-semibold" : ""} ${
        value < 0 ? "text-red-700" : ""
      }`}
    >
      {dash ? clpOrDash(value) : clp(value)}
    </TableCell>
  );
}

/** The four numeric cells shared by data / subtotal / total rows. */
function AmountCells({ a, bold }: { a: LibroComprasAmounts; bold?: boolean }) {
  return (
    <>
      <AmountCell value={a.netoAfecto} bold={bold} />
      <AmountCell value={a.netoExento} bold={bold} />
      <AmountCell value={a.ivaCredito} bold={bold} />
      <AmountCell value={a.total} dash={false} bold={bold} />
    </>
  );
}

function SectionRows({ section }: { section: LibroComprasSection }) {
  return (
    <>
      <TableRow className="bg-muted/60 hover:bg-muted/60">
        <TableCell colSpan={10} className="font-bold text-gray-800">
          {section.title}
        </TableCell>
      </TableRow>
      {section.lines.map((line) => (
        <TableRow key={line.id} className="hover:bg-muted/30">
          <TableCell className="text-muted-foreground">
            {line.siiCode ?? "-"}
          </TableCell>
          <TableCell>{line.folio}</TableCell>
          <TableCell className="text-muted-foreground">
            {line.corrInterno || "-"}
          </TableCell>
          <TableCell className="text-muted-foreground">
            {fmtDate(line.emissionDate)}
          </TableCell>
          <TableCell className="text-muted-foreground">
            {formatRutWithDots(line.rut)}
          </TableCell>
          <TableCell className="font-medium text-gray-800">
            {line.razonSocial || "-"}
          </TableCell>
          <AmountCells a={line} />
        </TableRow>
      ))}
      <TableRow className="bg-muted/30 hover:bg-muted/30">
        <TableCell colSpan={6} className="font-semibold">
          Total {section.title}
        </TableCell>
        <AmountCells a={section} bold />
      </TableRow>
    </>
  );
}

export function LibroComprasPage() {
  const { data: companies = [] } = useCompanies();
  const [companyId, setCompanyId] = useState<string>(ALIACE_SPA_ID);
  const [from, setFrom] = useState(() =>
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [to, setTo] = useState(() => dayjs().format("YYYY-MM-DD"));

  const { data: rows = [], isLoading } = useLibroCompras(companyId, from, to);

  const report = useMemo(() => buildLibroComprasReport(rows), [rows]);

  const companyName =
    companies.find((c) => c.id === companyId)?.name ?? "Empresa";

  const handleExport = () =>
    exportLibroComprasToExcel(report, { empresa: companyName, from, to });

  const { grandTotal } = report;

  return (
    <PageContainer>
      <PageHeader
        title="Libro de Compras"
        description="Facturas de proveedor contabilizadas por empresa y período (SII / F29)"
        actions={
          <Button
            variant="outline"
            className="h-9"
            onClick={handleExport}
            disabled={report.docCount === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>
        }
      />

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="grid gap-1.5">
              <Label htmlFor="lc-empresa">Empresa</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger id="lc-empresa" className="h-9 w-56">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="lc-desde">Desde</Label>
              <Input
                id="lc-desde"
                type="date"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
                className="h-9 w-44"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="lc-hasta">Hasta</Label>
              <Input
                id="lc-hasta"
                type="date"
                value={to}
                min={from}
                onChange={(e) => setTo(e.target.value)}
                className="h-9 w-44"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <TableSkeleton rows={8} columns={10} />
      ) : report.docCount === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={FileText}
              title="Sin facturas en el período"
              description="No hay facturas de proveedor para esta empresa en el rango seleccionado."
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Documentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{report.docCount}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Neto afecto
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{clp(grandTotal.netoAfecto)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  IVA crédito fiscal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{clp(grandTotal.ivaCredito)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{clp(grandTotal.total)}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cód. SII</TableHead>
                    <TableHead>N° Doc.</TableHead>
                    <TableHead>Corr. Interno</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>RUT</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="text-right">Neto Afecto</TableHead>
                    <TableHead className="text-right">Neto Exento</TableHead>
                    <TableHead className="text-right">IVA Crédito</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.sections.map((section) => (
                    <SectionRows key={section.code} section={section} />
                  ))}
                  <TableRow className="border-t-2 bg-muted/60 hover:bg-muted/60">
                    <TableCell colSpan={6} className="font-bold">
                      Total General
                    </TableCell>
                    <AmountCells a={grandTotal} bold />
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </PageContainer>
  );
}
